/**
 * Iter XX.267c — Lightweight page-level error boundary.
 *
 * Purpose: on physical iPhone builds, an unhandled render exception
 * inside a page component silently collapses to the neutral-950 body
 * background — the user sees a "blank black screen" with no signal.
 * PulsePage in particular reported this behaviour on WKWebView even
 * though the same page rendered fine on web preview.
 *
 * This boundary:
 *   · never masks a working page — only replaces the render tree
 *     when a real exception is thrown
 *   · shows a compact, visible fallback UI so QA can immediately
 *     tell the difference between "component crashed" and "route
 *     didn't mount"
 *   · surfaces the error text so the next physical-device build can
 *     paste it into a bug report without needing Xcode
 *   · leaves route + navigation intact — user can still tap Home
 *     in the bottom nav to leave the crashed surface
 *
 * Not a general-purpose global boundary — mount ONLY around
 * page-level surfaces that were reported blank.
 */
import React from "react";

export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }
  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: String(error?.message || error || "Unknown error"),
      stack: String(error?.stack || "").split("\n").slice(0, 6).join("\n"),
    };
  }
  componentDidCatch(error, info) {
    // Best-effort console signal so Safari Web Inspector picks it up.
    try {
      // eslint-disable-next-line no-console
      console.error("[XX.267c-page-error]", {
        page: this.props.pageName || "unknown",
        message: this.state.message,
        stack: this.state.stack,
        component_stack: info?.componentStack,
      });
    } catch { /* noop */ }
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    const pageName = this.props.pageName || "This page";
    return (
      <div
        data-testid="page-error-boundary"
        className="min-h-screen bg-neutral-950 text-neutral-100 px-6"
        style={{ paddingTop: "calc(2rem + env(safe-area-inset-top, 0px))" }}
      >
        <div className="max-w-md mx-auto">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            Something broke
          </p>
          <h1 className="text-2xl font-medium text-neutral-100 leading-tight mb-3">
            {pageName} couldn&apos;t load.
          </h1>
          <p className="text-sm text-neutral-400 leading-relaxed mb-6">
            You can head back and try again — the rest of Gully is
            still working.
          </p>
          <a
            href="/chat"
            data-testid="page-error-back"
            className="inline-flex items-center justify-center py-3 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
          >
            Back to Gully
          </a>
          <details className="mt-8 text-xs text-neutral-600">
            <summary className="cursor-pointer">Diagnostic</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-neutral-500">
              {this.state.message}
              {this.state.stack ? "\n\n" + this.state.stack : ""}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
